import { useRef, useState, useCallback } from "react";
import { cn } from "../lib/utils";

interface OTPInputProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  masked?: boolean;
}

export function OTPInput({
  length = 6,
  value = "",
  onChange,
  onComplete,
  disabled = false,
  placeholder = "·",
  className,
  masked = false,
}: OTPInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.padEnd(length, "").slice(0, length).split("");

  const focus = (i: number) => {
    const el = inputsRef.current[i];
    if (el) { el.focus(); el.select(); }
  };

  const update = useCallback(
    (newDigits: string[]) => {
      const joined = newDigits.join("").replace(/[^0-9]/g, "");
      onChange?.(joined);
      if (joined.length === length) onComplete?.(joined);
    },
    [length, onChange, onComplete]
  );

  const handleChange = (i: number, raw: string) => {
    const char = raw.replace(/[^0-9]/g, "").slice(-1);
    const next = [...digits];
    next[i] = char;
    update(next);
    if (char && i < length - 1) focus(i + 1);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...digits];
      if (next[i]) {
        next[i] = "";
        update(next);
      } else if (i > 0) {
        next[i - 1] = "";
        update(next);
        focus(i - 1);
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focus(i - 1);
    } else if (e.key === "ArrowRight" && i < length - 1) {
      e.preventDefault();
      focus(i + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, length);
    const next = pasted.padEnd(length, "").slice(0, length).split("");
    update(next);
    focus(Math.min(pasted.length, length - 1));
  };

  return (
    <div className={cn("otp-root", className)} onPaste={handlePaste}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el; }}
          className={cn("otp-cell", digit && "otp-cell-filled")}
          type={masked ? "password" : "text"}
          inputMode="numeric"
          maxLength={1}
          value={digit}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="one-time-code"
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          aria-label={`Digit ${i + 1} of ${length}`}
        />
      ))}
    </div>
  );
}
